import { useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { useTranslation } from '@/i18n';
import type { Key } from '@/i18n';
import { ApiError } from '@/lib/api/types';
import { cx } from '@/lib/cx';
import { AIRLINE_LENGTHS, icaoOf } from '@/types/airline';
import type { Airline } from '@/types/airline';
import { createAirline, deleteAirline, editAirline } from './api';
import type { AirlineWriteResult } from './api';
import styles from './AirlinePanel.module.css';

/**
 * One airline, in a dialog over the table — read, created or edited.
 *
 * **An edit is a replace.** The API writes `null` or `false` for every field the body
 * does not carry, so the body is built by spreading the row that was LOADED and
 * overwriting only the inputs. Building it from the form's own fields would look
 * identical on screen and clear anything the form does not model.
 *
 * **Deletion is not soft, and this table has no active flag.** The row goes, and
 * schedules keep the carrier's code as plain text with no foreign key — so a deleted
 * carrier leaves those flights with a code that resolves to nothing. Dropping a
 * carrier from the working list is what `kzFlying` is for, and that is what this panel
 * offers first.
 *
 * The five length rules are checked here as well as on the server. That is not
 * duplicated logic for its own sake: the columns are fixed-length `bpchar`, the API
 * documents the exact lengths, and a round trip to be told a code is one character
 * short is a round trip that did not need to happen.
 */

interface AirlinePanelProps {
  /** The airline being edited, or null for the create form. */
  airline: Airline | null;
  /** Whether this role may write. `false` renders the same fields, read-only. */
  canWrite: boolean;
  onSaved: (airline: Airline, action: 'created' | 'saved') => void;
  onDeleted: (airline: Airline) => void;
  onClose: () => void;
}

/** The form's fields, all as strings — an input has no other kind of value. */
interface Draft {
  iataCode: string;
  icaoCode: string;
  prefix: string;
  title: string;
  fullTitle: string;
  description: string;
  kzFlying: boolean;
}

const text = (value: unknown): string => (typeof value === 'string' ? value : '');

const draftFrom = (airline: Airline | null): Draft => ({
  iataCode: text(airline?.iataCode).trim(),
  // Trimmed, because a row seeded before this API existed carries three spaces rather
  // than a null — and an input holding `'   '` looks empty while failing the
  // exactly-three rule on save.
  icaoCode: airline ? (icaoOf(airline) ?? '') : '',
  prefix: text(airline?.prefix).trim(),
  title: text(airline?.title),
  fullTitle: text(airline?.fullTitle),
  description: text(airline?.description),
  kzFlying: airline?.kzFlying ?? false,
});

/**
 * The form's fields as the API names them.
 *
 * The three codes are upper-cased here as well as on the server, so what the form
 * shows after a save is what the dictionary holds. An empty ICAO code or prefix is
 * sent as `null` rather than `""`: the server stores blanks as null anyway, and a
 * `""` in a fixed-length column is the space-padding that produced the legacy rows
 * this form has to trim on the way in.
 */
const bodyFrom = (draft: Draft): Partial<Airline> => ({
  iataCode: draft.iataCode.trim().toUpperCase(),
  icaoCode: blankToNull(draft.icaoCode.trim().toUpperCase()),
  prefix: blankToNull(draft.prefix.trim().toUpperCase()),
  title: draft.title.trim(),
  fullTitle: blankToNull(draft.fullTitle.trim()),
  description: blankToNull(draft.description.trim()),
  kzFlying: draft.kzFlying,
});

const blankToNull = (value: string): string | null => (value === '' ? null : value);

export const AirlinePanel = ({
  airline,
  canWrite,
  onSaved,
  onDeleted,
  onClose,
}: AirlinePanelProps) => {
  const { t } = useTranslation();

  const isCreate = airline === null;

  // No sync effect: the panel is keyed by the selection in AirlinesPage, so choosing
  // another row remounts it and this initialiser runs again. An effect copying the
  // prop into state would set state during the commit and overwrite what is being
  // typed.
  const [draft, setDraft] = useState<Draft>(() => draftFrom(airline));
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof Draft, string>>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  /**
   * Whether closing now would throw away something typed. While it would, a click
   * beside the dialog does nothing; Escape, Cancel and × still close it.
   */
  const isDirty = JSON.stringify(draft) !== JSON.stringify(draftFrom(airline));

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) => {
    setDraft((current) => ({ ...current, [key]: value }));
    // Both messages the form can carry are verdicts on a submission that no longer
    // describes what is on screen.
    setFieldErrors((current) => ({ ...current, [key]: undefined }));
    setFormError(null);
  };

  /** Runs a write and reports it, so create and edit share one path. */
  const run = async (write: () => Promise<AirlineWriteResult>, action: 'created' | 'saved') => {
    setFormError(null);
    setIsSubmitting(true);

    try {
      const result = await write();

      if (result.ok) {
        onSaved(result.airline, action);
        return;
      }

      // The server's own sentence — a duplicate code, a bad length, an id that is
      // gone. It arrives in Russian and is shown as written: rewording a message that
      // names a specific code would lose the part that identifies it.
      setFormError(result.message ?? t('airlines.panel.saveFailed'));
    } catch (caught) {
      setFormError(messageFor(caught, t));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const body = bodyFrom(draft);
    const problems = validate(draft, t);

    if (Object.keys(problems).length > 0) {
      setFieldErrors(problems);
      setFormError(null);
      return;
    }

    setFieldErrors({});

    if (airline === null) {
      void run(() => createAirline(body), 'created');
      return;
    }

    // THE WHOLE ROW, with the form applied over it — see the note at the top of this
    // file. Every field absent from this object would be written as null or false.
    void run(() => editAirline(airline.id, { ...airline, ...body }), 'saved');
  };

  const handleDelete = async () => {
    if (!airline) return;

    setFormError(null);
    setIsSubmitting(true);

    try {
      const result = await deleteAirline(airline.id);

      if (result.ok) {
        onDeleted(airline);
        return;
      }

      setFormError(result.message ?? t('airlines.panel.deleteFailed'));
      setConfirmingDelete(false);
    } catch (caught) {
      setFormError(messageFor(caught, t));
      setConfirmingDelete(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const invalid = (field: keyof Draft) =>
    fieldErrors[field] ? { error: fieldErrors[field] } : {};

  /** Every text input on this form takes the same three props. */
  const common = {
    disabled: isSubmitting,
    readOnly: !canWrite,
    autoComplete: 'off' as const,
  };

  const content = (
    <>
      <header className={styles.header}>
        <div className={styles.identity}>
          <span className={styles.code}>
            {isCreate ? t('airlines.panel.create') : (airline.iataCode ?? t('airlines.panel.noCode'))}
          </span>

          {!isCreate && (
            <>
              <span className={styles.name}>{airline.title ?? ''}</span>
              <div className={styles.badges}>
                <Badge tone={airline.kzFlying ? 'success' : 'neutral'}>
                  {t(airline.kzFlying ? 'airlines.states.working' : 'airlines.states.notWorking')}
                </Badge>
                {icaoOf(airline) !== null && <Badge code>{icaoOf(airline)}</Badge>}
              </div>
            </>
          )}
        </div>

        <button
          type="button"
          className={styles.close}
          onClick={onClose}
          aria-label={t('common.close')}
        >
          ×
        </button>
      </header>

      <div className={styles.body}>
        {/* Said once, at the top, rather than as a disabled control the reader has to
            work out the reason for. */}
        {!canWrite && <p className={styles.readOnly}>{t('airlines.panel.readOnly')}</p>}

        <form className={styles.form} onSubmit={handleSubmit} noValidate>
          {formError && (
            // Announced when it appears: a reader whose focus is still in the field
            // that caused it would otherwise never learn why nothing happened.
            <p className={styles.error} role="alert">
              {formError}
            </p>
          )}

          <Section title={t('airlines.panel.codes')}>
            <Field
              label={t('airlines.columns.iata')}
              value={draft.iataCode}
              onChange={(event) => set('iataCode', event.target.value.toUpperCase())}
              {...invalid('iataCode')}
              maxLength={AIRLINE_LENGTHS.iataCode}
              required
              {...common}
            />
            <Field
              label={t('airlines.columns.icao')}
              value={draft.icaoCode}
              onChange={(event) => set('icaoCode', event.target.value.toUpperCase())}
              {...invalid('icaoCode')}
              maxLength={AIRLINE_LENGTHS.icaoCode}
              {...common}
            />
            <Field
              label={t('airlines.columns.prefix')}
              value={draft.prefix}
              onChange={(event) => set('prefix', event.target.value.toUpperCase())}
              {...invalid('prefix')}
              maxLength={AIRLINE_LENGTHS.prefix}
              {...common}
            />
          </Section>

          <Section title={t('airlines.panel.names')} wide>
            <Field
              label={t('airlines.panel.title')}
              value={draft.title}
              onChange={(event) => set('title', event.target.value)}
              {...invalid('title')}
              maxLength={AIRLINE_LENGTHS.title}
              required
              {...common}
            />
            <Field
              label={t('airlines.panel.fullTitle')}
              value={draft.fullTitle}
              onChange={(event) => set('fullTitle', event.target.value)}
              {...invalid('fullTitle')}
              maxLength={AIRLINE_LENGTHS.fullTitle}
              {...common}
            />
          </Section>

          <Section title={t('airlines.panel.details')} wide>
            <div className={styles.field}>
              <span className={styles.label}>{t('airlines.columns.working')}</span>
              <Select
                size="md"
                options={[
                  { value: 'true', label: t('airlines.states.working') },
                  { value: 'false', label: t('airlines.states.notWorking') },
                ]}
                value={draft.kzFlying ? 'true' : 'false'}
                onChange={(event) => set('kzFlying', event.target.value === 'true')}
                disabled={isSubmitting || !canWrite}
                aria-label={t('airlines.columns.working')}
              />
            </div>

            <div className={styles.field}>
              <span className={styles.label}>{t('airlines.panel.description')}</span>
              <textarea
                className={styles.textarea}
                value={draft.description}
                onChange={(event) => set('description', event.target.value)}
                rows={3}
                // The one field with no length limit — a `text` column.
                disabled={isSubmitting}
                readOnly={!canWrite}
                aria-label={t('airlines.panel.description')}
              />
            </div>
          </Section>

          {/* No created/updated stamps: this table keeps none, so there is nothing
              honest to put here. */}

          {canWrite && (
            <div className={styles.actions}>
              <Button type="submit" variant="primary" disabled={isSubmitting}>
                {isSubmitting
                  ? t('airlines.panel.saving')
                  : t(isCreate ? 'airlines.panel.createAction' : 'common.save')}
              </Button>
              <Button onClick={onClose} disabled={isSubmitting}>
                {t('common.cancel')}
              </Button>
            </div>
          )}
        </form>

        {/*
          Outside the form, and last.

          The row is REMOVED, and nothing cleans up after it: schedules hold the code
          as text with no foreign key, so every flight already flown under this carrier
          keeps a code that resolves to nothing. Two steps, and the safer alternative
          named where the reader is standing.
        */}
        {canWrite && !isCreate && (
          <section className={styles.danger}>
            <div className={styles.dangerText}>
              <h3 className={styles.dangerTitle}>{t('airlines.panel.deleteTitle')}</h3>
              <p className={styles.dangerHint}>
                {confirmingDelete
                  ? t('airlines.panel.deleteConfirm', {
                      code: airline.iataCode ?? String(airline.id),
                    })
                  : t('airlines.panel.deleteHint')}
              </p>
            </div>

            {confirmingDelete ? (
              <div className={styles.dangerActions}>
                <Button variant="danger" onClick={() => void handleDelete()} disabled={isSubmitting}>
                  {t('airlines.panel.deleteConfirmAction')}
                </Button>
                <Button onClick={() => setConfirmingDelete(false)} disabled={isSubmitting}>
                  {t('common.cancel')}
                </Button>
              </div>
            ) : (
              <Button
                variant="danger"
                onClick={() => setConfirmingDelete(true)}
                disabled={isSubmitting}
              >
                {t('common.delete')}
              </Button>
            )}
          </section>
        )}
      </div>
    </>
  );

  /**
   * A dialog over the table, for a new carrier and an existing one alike.
   *
   * A column beside the table squeezed every row whenever a carrier was open, and
   * reflowed the table again on close. Over it, the table keeps its width the whole
   * time, and the form gets the room a form wants.
   */
  return (
    <Modal
      label={t(isCreate ? 'airlines.panel.create' : 'airlines.panel.edit')}
      onClose={onClose}
      dismissible={!isDirty && !isSubmitting}
    >
      {content}
    </Modal>
  );
};

/**
 * The rules the server enforces, checked before a request is spent on them.
 *
 * Only the ones the API states exactly. `maxLength` on the inputs already prevents a
 * value that is too long, so what is left is the two EXACT lengths and the two
 * required fields — the cases a reader can reach by typing too few characters.
 * Uniqueness is not checked here: only the server knows the dictionary.
 */
const validate = (
  draft: Draft,
  t: (key: Key, values?: Record<string, string | number>) => string,
): Partial<Record<keyof Draft, string>> => {
  const problems: Partial<Record<keyof Draft, string>> = {};

  const iata = draft.iataCode.trim();
  const icao = draft.icaoCode.trim();

  if (iata === '') problems.iataCode = t('common.required');
  else if (iata.length !== AIRLINE_LENGTHS.iataCode) {
    problems.iataCode = t('airlines.panel.iataLength', { count: AIRLINE_LENGTHS.iataCode });
  }

  // Optional, but exactly three when given — an empty box is a carrier without one.
  if (icao !== '' && icao.length !== AIRLINE_LENGTHS.icaoCode) {
    problems.icaoCode = t('airlines.panel.icaoLength', { count: AIRLINE_LENGTHS.icaoCode });
  }

  if (draft.title.trim() === '') problems.title = t('common.required');

  return problems;
};

/**
 * A failed request, told apart from a rejected one.
 *
 * A 403 here means this role may not write to the dictionary — it is not a validation
 * error, and offering "try again" would be wrong twice over.
 */
const messageFor = (caught: unknown, t: (key: Key) => string): string => {
  const status = caught instanceof ApiError ? caught.status : 0;

  if (status === 403) return t('airlines.panel.forbidden');
  return status === 0 ? t('airlines.panel.networkError') : t('airlines.panel.serverError');
};

const Section = ({
  title,
  wide = false,
  children,
}: {
  title: string;
  /** One column rather than two — for a name that needs the width. */
  wide?: boolean;
  children: ReactNode;
}) => (
  <section className={styles.section}>
    <h3 className={styles.sectionTitle}>{title}</h3>
    <div className={cx(styles.grid, wide && styles.gridWide)}>{children}</div>
  </section>
);
